import React, {useEffect, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import {useStore} from 'effector-react';
import {useForm, Controller} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import * as yup from 'yup';

import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {AsyncSelect} from 'components/ui-kit/Select';
import {
    gdsCreatingStepChanged,
    gdsStorageLocation$,
    goodsDeclarationDetails$,
    updateCustomsNameAndNumber,
    updateGdsStorageLocation,
} from 'models/documents/gds';

import {useCodeAndStorageFetch} from '../../hooks/useCodeAndStorageFetch';

import {CustomerSelectOption} from '../CustomSelectOption';

import {ControlItem, Form, FormControlWrapper, FormField} from './styled';

const baseLabelValueSchema = yup
    .object({
        label: yup.string().required(),
        value: yup.string().required(),
    })
    .required();

const gdFirstStepSchema = yup.object().shape({
    customsNameAndNumber: baseLabelValueSchema.required('Customer Company Name or TIN is required'),
    storageLocationName: baseLabelValueSchema.required('Storage location is required'),
});

type FormData = {
    customsNameAndNumber: {label: string; value: string} | null;
    storageLocationName: {label: string; value: string} | null;
};

export const GDFirstStep = ({allowToEdit}: {allowToEdit: boolean}) => {
    const {t} = useTranslation();
    const {customsNameAndNumber, gdsStorageLocation, gdsId} = useStore(goodsDeclarationDetails$);
    const {loadSelectValues, loadStorageLocationValues} = useCodeAndStorageFetch();

    const {
        control,
        handleSubmit,
        setValue,
        trigger,
        formState: {errors, isValid},
    } = useForm<FormData>({
        mode: 'onChange',
        resolver: yupResolver(gdFirstStepSchema),
        defaultValues: {
            customsNameAndNumber: null,
            storageLocationName: null,
        },
    });
    const onSubmit = () => {
        gdsCreatingStepChanged(1);
    };

    useEffect(() => {
        if (customsNameAndNumber.value) {
            setValue('customsNameAndNumber', customsNameAndNumber);
            trigger('customsNameAndNumber');
        }
    }, [customsNameAndNumber]);

    useEffect(() => {
        if (gdsStorageLocation.value) {
            setValue('storageLocationName', gdsStorageLocation);
            trigger('storageLocationName');
        }
    }, [gdsStorageLocation]);

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <FormField>
                <Controller
                    control={control}
                    name="customsNameAndNumber"
                    render={(props) => (
                        <AsyncSelect
                            size="sm"
                            {...props}
                            isRequired={true}
                            label={t('pages.documents.gds.firstStep.customsAuthorityCode')}
                            isSearchable={false}
                            defaultOptions
                            disabled={!allowToEdit}
                            menuPlacement={'bottom'}
                            menuPosition={'fixed'}
                            optionComponent={CustomerSelectOption}
                            loadOptions={async () => {
                                const value = await loadSelectValues();
                                if (gdsId) {
                                    const valueToSet = value.find(
                                        (item) =>
                                            item.value.toString() ===
                                            customsNameAndNumber.value.toString(),
                                    );
                                    setValue(
                                        'customsNameAndNumber',
                                        valueToSet ?? customsNameAndNumber,
                                    );
                                    trigger('customsNameAndNumber');
                                }
                                return value;
                            }}
                            placeholder={t('pages.documents.gds.firstStep.selectCode')}
                            hasError={!!errors.customsNameAndNumber}
                            onChange={(value: {value: number; label: string; number: string}) => {
                                props.onChange(value);
                                updateCustomsNameAndNumber(value);
                            }}
                        />
                    )}
                />
                <Controller
                    control={control}
                    name="storageLocationName"
                    render={(props) => (
                        <AsyncSelect
                            size="sm"
                            {...props}
                            label={t('pages.documents.gds.firstStep.storageLocation')}
                            isRequired={true}
                            isSearchable={false}
                            defaultOptions
                            disabled={!allowToEdit}
                            menuPlacement={'bottom'}
                            menuPosition={'fixed'}
                            loadOptions={async () => {
                                const value = await loadStorageLocationValues();
                                if (gdsId) {
                                    const valueToSet = value.find(
                                        (item) =>
                                            item.value.toString() ===
                                            gdsStorageLocation.value.toString(),
                                    );
                                    setValue(
                                        'storageLocationName',
                                        valueToSet ?? gdsStorageLocation,
                                    );
                                    trigger('storageLocationName');
                                }
                                return value;
                            }}
                            placeholder={t('pages.documents.gds.firstStep.selectStorageLocation')}
                            hasError={!!errors.storageLocationName}
                            onChange={(value: {value: string; label: string}) => {
                                props.onChange(value);
                                updateGdsStorageLocation(value);
                            }}
                        />
                    )}
                />
            </FormField>
            <FormControlWrapper>
                <ControlItem>
                    <Button
                        size="sm"
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={!isValid}
                    >
                        {t('pages.documents.gds.firstStep.nextStep')}
                    </Button>
                </ControlItem>
            </FormControlWrapper>
        </Form>
    );
};
